import * as React from 'react';
import { LeadServedSource } from '../../viewModels/queue';
import { toCoachMessage } from './coachMessage';
import { clearCachedConversationId, getCachedConversationId, setCachedConversationId } from './conversationCache';
import { CoachMessage, PendingKeywordTag } from './types';
import { useRealtimeCoaching } from './useRealtimeCoaching';

/**
 * Arguments for `useCoaching`. All fields are optional because the hook waits
 * for a `companyId` before bootstrapping; passing only `companyId` is enough
 * to start (or resume) a conversation for that lead.
 */
export type UseCoachingArgs = {
	companyId?: string;
	contactId?: string;
	contactName?: string;
	contactTitle?: string;
	phoneCallId?: string;
	leadServedId?: string;
	/**
	 * True once the lead record for `companyId` has settled, so `leadServedId` is final (present, or
	 * genuinely absent). A fresh conversation is not created before this: the server attaches the
	 * pre-written Game Plan only when the conversation carries `leadServedId` by the auto-start turn.
	 */
	isLeadLoaded?: boolean;
	leadServedSource?: LeadServedSource;
};

export type UseCoachingApi = {
	messages: CoachMessage[];
	isResponding: boolean;
	isLoadingHistory: boolean;
	/** True if the active conversation was resumed from cache rather than freshly created. */
	wasResumed: boolean;
	/** Stable id for the active conversation, or null while bootstrapping. */
	conversationId: string | null;
	/** Server-resolved coaching template of a freshly created conversation, or null when unknown. */
	agentTemplateName: string | null;
	/**
	 * True while the bootstrap `createNew` POST is in flight. Exposed so the
	 * pre-dial gate (`waitForCoachingConversation`) can distinguish "still
	 * creating, keep waiting" from "create failed / never started" — mirrors
	 * the legacy queue's `coaching.isCreatingConversation` check in
	 * `CallButton`/`startCoachingBeforeCall`.
	 */
	isCreatingConversation: boolean;
	/**
	 * Send a composer message to the coach. When `tag` is provided, the next
	 * incoming assistant message is decorated with `pinnedKeyword` + `pinColor`
	 * so the UI can render the pinned-keyword ribbon.
	 */
	send(text: string, tag?: PendingKeywordTag): void;
	/**
	 * Send the opening auto-start turn of a fresh conversation, ahead of anything queued. The server
	 * replies with the lead's stored Game Plan; `text` (the Game Plan pin's prompt) is optional.
	 */
	sendOpening(text?: string, tag?: PendingKeywordTag): void;
	/**
	 * Send an automatic keyword pin (`before_call` / `on_dial`). Like {@link send}, but held until the
	 * conversation's opening turn has been sent and its reply has finished.
	 */
	sendAutomatic(text: string, tag?: PendingKeywordTag): void;
	/** True from conversation create or history load until the opening (Game Plan) reply finishes. */
	isWaitingForGamePlan: boolean;
	/** Append a finalized transcript entry to the conversation message stream. */
	addTranscriptEntry(entry: { id: string; speaker: string; text: string; timestamp: Date }): void;
	/**
	 * Enqueue an "ask the AI for a suggestion based on recent transcript"
	 * dispatch. Goes through the same send-queue as direct sends so it never
	 * overlaps an in-flight stream. No-op if no transcript has accumulated
	 * since the last suggestion / resume.
	 */
	requestAutoSuggestion(): void;
	/**
	 * Manually request a coaching suggestion based on accumulated transcript.
	 * Alias of {@link requestAutoSuggestion} surfaced for the "Get suggestion"
	 * button rendered when `coffeePreferences.autoSuggestionsEnabled === false`.
	 * Same queue + dedup semantics — at most one suggestion can be queued at a
	 * time.
	 */
	requestSuggestion(): void;
	/**
	 * Manually request the end-of-call summary. Mirrors {@link endCall} but
	 * skips the "transcript-required" guard — the user has explicitly clicked
	 * "Get summary" so we trust their intent. Routes through the same send
	 * queue so a concurrent in-flight stream (Game Plan, last suggestion) is
	 * allowed to finish first; deduped against any already-queued summary.
	 */
	requestPostCallSummary(): void;
	/**
	 * Drop the cached conversation for this lead and bootstrap a brand-new one.
	 * The next render of `useCoaching` will see no cached id and call
	 * `realtime.createNew` again, which re-fires the `before_call` keyword pins
	 * and clears the message stream. Use sparingly — this is the "start fresh"
	 * escape hatch from the center column header.
	 */
	startNewConversation(): void;
	/**
	 * Request the end-of-call summary and tear down the session. Returns whether
	 * a summary was actually queued — `false` means the call carried no
	 * transcript, and the caller should keep the manual "Get summary" affordance
	 * on screen as an override.
	 */
	endCall(): boolean;
};

/**
 * Focused-view coaching adapter. Owns conversation lifecycle (create / resume
 * / cache), keyword tags, send-queue gate, and the upstream→view
 * message normalization. Wraps `useRealtimeCoaching` (the focused view's own
 * realtime hook) — the legacy `TysonCoachingContext` is no longer involved.
 *
 * Bootstrap policy:
 *   1. Wait for `companyId` to land.
 *   2. Look up `companyId → conversationId` in localStorage. If present,
 *      `resume()` the existing conversation and skip the create POST.
 *   3. Otherwise wait for `isLeadLoaded`, then `createNew({ companyId, leadServedId, ... })`
 *      and cache the new id keyed by `companyId` so the next reload resumes.
 *   4. On 4xx during history backfill (stale cache), the caller should clear
 *      the cache entry and retry — handled by `useRealtimeCoaching`'s
 *      `historyQuery` failing silently; future iteration: surface this here.
 *
 * A keyword tag is bound to the request id of the send that carried it, and a
 * reply carries the request id of the stream that produced it, so each tag lands
 * on exactly that reply. See pins/README.md "End-to-end flow".
 */
export function useCoaching(args: UseCoachingArgs): UseCoachingApi {
	// Pass `companyId` so the realtime hook attaches `SalesLeadAIContext` on
	// every outbound stream — without this the AI doesn't see lead data even
	// though the conversation was created with `companyId`.
	const realtime = useRealtimeCoaching({ companyId: args.companyId });

	// `conversationId` is the conversation the item was queued for; it is dropped if another one is active by the time it drains.
	type QueueItem =
		| { kind: 'opening'; text?: string; tag?: PendingKeywordTag; conversationId: string | null }
		| {
				kind: 'direct';
				text: string;
				tag?: PendingKeywordTag;
				afterOpening?: boolean;
				conversationId: string | null;
		  }
		| { kind: 'autoSuggestion' }
		| { kind: 'summary' };
	const tagByRequestIdRef = React.useRef<Map<string, PendingKeywordTag>>(new Map());
	const sendQueueRef = React.useRef<QueueItem[]>([]);
	const inFlightRef = React.useRef(false);
	const upstreamRef = React.useRef(realtime.coachingMessages);
	// Track which (companyId, contactId) pair we've already auto-bootstrapped
	// for so we don't fire two conversations on re-renders.
	const startedKeyRef = React.useRef<string | null>(null);
	// Tracks the last `companyId` we bootstrapped for. When the rep switches
	// leads (Search → click result, Next lead, inbound CallFromUnknown
	// attach), this lets the bootstrap effect detect the company change and
	// reset the conversation — otherwise the `realtime.conversationId` guard
	// below would keep the panel pinned to the previous lead's conversation.
	const lastCompanyIdRef = React.useRef<string | null>(null);

	const upstream = realtime.coachingMessages;
	upstreamRef.current = upstream;

	// Drop the send + tag queues and release the in-flight lock. Used both by
	// `startNewConversation` (explicit "start fresh") and the lead-switch branch
	// of the bootstrap effect below. Without this on lead switch, a send or
	// post-call summary queued / in flight on the previous lead drains into the
	// next lead's freshly-created conversation, and a pending keyword tag claims
	// the next lead's first assistant message.
	const clearSendAndTagQueues = React.useCallback(() => {
		sendQueueRef.current = [];
		tagByRequestIdRef.current = new Map();
		inFlightRef.current = false;
	}, []);

	// Auto-bootstrap. Resume an existing conversation for this companyId if
	// one is cached; otherwise create a new one and cache the id for next
	// reload.
	React.useEffect(() => {
		const { companyId, contactId, phoneCallId, leadServedId, isLeadLoaded, leadServedSource } = args;
		if (!companyId) return;

		// Lead switched since the last bootstrap. Reset realtime state so the
		// new lead's cached / freshly-created conversation doesn't inherit
		// messages from the prior lead, then fall through to resume/createNew
		// in the same effect run. Returning early here (the prior behaviour)
		// left a render gap where the old conversation was still active.
		const isLeadSwitch = !!lastCompanyIdRef.current && lastCompanyIdRef.current !== companyId;
		if (isLeadSwitch) {
			realtime.reset();
			// Same teardown as `startNewConversation` — otherwise a queued / in-
			// flight send or post-call summary from the prior lead drains into
			// this lead's conversation once the old stream ends.
			clearSendAndTagQueues();
			startedKeyRef.current = null;
		}

		lastCompanyIdRef.current = companyId;

		// When the bootstrap has already produced a conversation for *this*
		// lead, nothing to do. Read the realtime refs, not this render's values:
		// a create can land between this render and its effect, and trusting the
		// stale `conversationId` created a second conversation for the lead. On a
		// lead switch `realtime.reset()` above has already cleared both refs.
		if (realtime.getConversationId() || realtime.getIsCreating()) return;

		const key = `${companyId}:${contactId ?? '-'}`;
		if (startedKeyRef.current === key) return;

		const cached = getCachedConversationId(companyId);
		// Only resume a cached conversation for inbound calls; every outbound serve starts
		// fresh so the prior call's gameplan doesn't reappear and stale context can't accumulate.
		if (cached && leadServedSource === LeadServedSource.IncomingCall) {
			startedKeyRef.current = key;
			realtime.resume(cached);
			return;
		}
		if (!isLeadLoaded) return;
		startedKeyRef.current = key;
		void realtime.createNew({ companyId, phoneCallId, leadServedId }).then(id => {
			if (id) setCachedConversationId(companyId, id);
		});
	}, [args, realtime, clearSendAndTagQueues]);

	// Auto-enrich the existing conversation with the active `phoneCallId` when
	// a real call goes live. Mirrors legacy `useRealCallCoachingSession`
	// (lines 38-45). The bootstrap above only carries the phoneCallId on the
	// FIRST `createNew` — if coaching was created before the call started (or
	// resumed from cache), the conversation has no phoneCallId until this
	// effect stitches it in. A ref dedupes against the same id so a flurry of
	// re-renders during the call only enriches once.
	const lastEnrichedPhoneCallIdRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		const { phoneCallId } = args;
		if (!phoneCallId) return;
		if (!realtime.conversationId) return;
		if (lastEnrichedPhoneCallIdRef.current === phoneCallId) return;
		lastEnrichedPhoneCallIdRef.current = phoneCallId;
		realtime.enrichCreationContext({ phoneCallId });
	}, [args, realtime]);

	// Auto-enrich the conversation with `leadServedId` when it changes after the
	// conversation exists (a resumed inbound conversation, or a lead refetch).
	// Fresh conversations already carry it on the create POST, since the
	// bootstrap waits for `isLeadLoaded`. The dedup key includes the
	// conversationId (unlike the phoneCallId enrich above) because lead
	// switches ALWAYS change `leadServedId` while the old conversation is still
	// rendered — keying on the pair ensures the enrich re-fires once the NEW
	// conversation lands instead of being swallowed by the dedup.
	const lastEnrichedLeadServedKeyRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		const { leadServedId } = args;
		if (!leadServedId) return;
		if (!realtime.conversationId) return;
		const key = `${realtime.conversationId}:${leadServedId}`;
		if (lastEnrichedLeadServedKeyRef.current === key) return;
		lastEnrichedLeadServedKeyRef.current = key;
		realtime.enrichCreationContext({ leadServedId });
	}, [args, realtime]);

	const messages = React.useMemo<CoachMessage[]>(
		() => upstream.map(m => toCoachMessage(m, tagByRequestIdRef.current)),
		[upstream]
	);

	// Pull the next queued send and dispatch it, if nothing is currently in flight.
	// Auto-suggestion items can be no-ops if no transcript has accumulated; in
	// that case we release the lock immediately and try the next item rather
	// than wait for a stream-end transition that never comes.
	const drainSendQueue = React.useCallback(() => {
		const tagRequest = (requestId: string | null, tag: PendingKeywordTag | undefined) => {
			if (requestId && tag) tagByRequestIdRef.current.set(requestId, tag);
		};
		if (inFlightRef.current) return;
		const head = sendQueueRef.current[0];
		if (!head) return;
		// Held on the opening phase itself, not only the stream lock, so the Game Plan reply always lands first.
		if (head.kind === 'direct' && head.afterOpening && realtime.getOpeningPhase() !== 'none') return;
		const next = sendQueueRef.current.shift();
		if (!next) return;
		if ('conversationId' in next && next.conversationId && next.conversationId !== realtime.getConversationId()) {
			drainSendQueue();
			return;
		}
		inFlightRef.current = true;
		if (next.kind === 'opening') {
			if (next.text) {
				tagRequest(realtime.sendDirectQuestion(next.text), next.tag);
			} else if (!realtime.sendOpening()) {
				inFlightRef.current = false;
				drainSendQueue();
			}
			return;
		}
		if (next.kind === 'direct') {
			tagRequest(realtime.sendDirectQuestion(next.text), next.tag);
			return;
		}
		if (next.kind === 'summary') {
			realtime.requestPostCallSummary();
			return;
		}
		// kind === 'autoSuggestion'
		const flushed = realtime.sendTranscriptSuggestion();
		if (flushed === 0) {
			inFlightRef.current = false;
			drainSendQueue();
		}
	}, [realtime]);

	// Watch the AI-responding flag. A true→false transition releases the
	// in-flight lock and dispatches the next queued send.
	//
	// On a lead switch the bootstrap effect above aborts the prior lead's stream,
	// which flips this flag true→false and fires this drain. That's safe because
	// the bootstrap effect (declared earlier, so it runs first) has already
	// emptied the send queue via `clearSendAndTagQueues()` — so the drain finds
	// nothing to dispatch into the new lead. Keep that ordering: this watcher
	// must stay declared after the bootstrap effect.
	const wasRespondingRef = React.useRef(false);
	React.useEffect(() => {
		const isResponding = realtime.isAIResponding;
		if (wasRespondingRef.current && !isResponding) {
			inFlightRef.current = false;
			drainSendQueue();
		}
		wasRespondingRef.current = isResponding;
	}, [realtime.isAIResponding, drainSendQueue]);

	const prevOpeningPhaseRef = React.useRef(realtime.openingPhase);
	React.useEffect(() => {
		const wasOpening = prevOpeningPhaseRef.current !== 'none';
		prevOpeningPhaseRef.current = realtime.openingPhase;
		if (wasOpening && realtime.openingPhase === 'none') drainSendQueue();
	}, [realtime.openingPhase, drainSendQueue]);

	const enqueueDirect = React.useCallback(
		(text: string, tag: PendingKeywordTag | undefined, afterOpening: boolean) => {
			const trimmed = text.trim();
			if (!trimmed) return;
			sendQueueRef.current.push({
				kind: 'direct',
				text: trimmed,
				tag,
				afterOpening,
				conversationId: realtime.getConversationId(),
			});
			drainSendQueue();
		},
		[drainSendQueue, realtime]
	);

	const sendOpening = React.useCallback(
		(text?: string, tag?: PendingKeywordTag) => {
			// Jumps the queue: automatic pins queued ahead of it would otherwise wait on an opening that never goes.
			sendQueueRef.current.unshift({
				kind: 'opening',
				text: text?.trim() || undefined,
				tag,
				conversationId: realtime.getConversationId(),
			});
			drainSendQueue();
		},
		[drainSendQueue, realtime]
	);

	const send = React.useCallback(
		(text: string, tag?: PendingKeywordTag) => enqueueDirect(text, tag, false),
		[enqueueDirect]
	);

	const sendAutomatic = React.useCallback(
		(text: string, tag?: PendingKeywordTag) => enqueueDirect(text, tag, true),
		[enqueueDirect]
	);

	const requestAutoSuggestion = React.useCallback(() => {
		// At most one suggestion can be queued at a time. If something else is
		// already queued or in flight (a direct send, or another suggestion),
		// the lead's continued speech keeps appending to `pendingTranscriptRef`
		// inside the realtime hook — when the queued suggestion finally fires,
		// it batches the entire accumulated transcript in one request rather
		// than triggering a second AI reply per utterance.
		const hasPendingSuggestion = sendQueueRef.current.some(item => item.kind === 'autoSuggestion');
		if (hasPendingSuggestion) return;
		sendQueueRef.current.push({ kind: 'autoSuggestion' });
		drainSendQueue();
	}, [drainSendQueue]);

	const startNewConversation = React.useCallback(() => {
		const { companyId } = args;
		if (companyId) clearCachedConversationId(companyId);
		// Drop send + tag queues so a stale before-call burst from the previous
		// conversation doesn't carry over into the fresh one.
		clearSendAndTagQueues();
		// Force the auto-bootstrap effect to run again.
		startedKeyRef.current = null;
		realtime.reset();
	}, [args, realtime, clearSendAndTagQueues]);

	// Internal helper: queue a post-call summary unless one is already queued
	// or in flight. Dedup so a flurry of clicks / repeated `call:ended` events
	// from Twilio (reconnects, glitches) only summarizes once.
	const queueSummary = React.useCallback(() => {
		const hasPendingSummary = sendQueueRef.current.some(item => item.kind === 'summary');
		if (hasPendingSummary) return;
		sendQueueRef.current.push({ kind: 'summary' });
		drainSendQueue();
	}, [drainSendQueue]);

	const endCall = React.useCallback((): boolean => {
		// Only request the post-call summary if real transcription happened.
		// Without this guard, dial failures (e.g. Twilio gateway 31005 immediately
		// after `device.connect()`) trigger `call:ended` while the auto-Game-Plan
		// is still streaming, and the summary POST collides with the open stream
		// for a 409.
		//
		// The check is against the local transcript messages, not the unsent
		// `pendingTranscriptRef` inside the realtime hook: with auto-suggestions on,
		// a call that ends right after a suggestion has nothing pending yet clearly
		// had transcription, and still deserves a summary.
		const hasTranscript = upstreamRef.current.some(m => m.messageType === 'transcript');
		if (!hasTranscript) return false;
		// Route through the send queue so the summary waits for any in-flight
		// stream (Game Plan, last auto-suggestion, etc.) to finish — Anthropic
		// 409s a concurrent POST to the same conversation, and the AI provider
		// 429s burst writes.
		queueSummary();
		return true;
	}, [queueSummary]);

	// Public manual trigger for "Get summary". Skips the transcript guard
	// because the rep has explicitly clicked the button — we trust their
	// intent even if the transcript pipeline missed entries.
	const requestPostCallSummary = React.useCallback(() => {
		queueSummary();
	}, [queueSummary]);

	return {
		messages,
		isResponding: realtime.isAIResponding,
		isLoadingHistory: realtime.isLoadingHistory,
		wasResumed: realtime.wasResumed,
		conversationId: realtime.conversationId,
		agentTemplateName: realtime.agentTemplateName,
		isCreatingConversation: realtime.isCreatingConversation,
		send,
		sendOpening,
		sendAutomatic,
		isWaitingForGamePlan:
			realtime.isCreatingConversation || realtime.isLoadingHistory || realtime.openingPhase !== 'none',
		addTranscriptEntry: realtime.addTranscriptEntry,
		requestAutoSuggestion,
		requestSuggestion: requestAutoSuggestion,
		requestPostCallSummary,
		startNewConversation,
		endCall,
	};
}
